import { Link } from 'react-router-dom'
import {
  CalendarDays,
  CalendarClock,
  ClipboardList,
  ShoppingBag,
  Users,
  Music2,
  Plus,
  Camera,
  MapPin,
  Tag,
} from 'lucide-react'

import { useTenant } from '@/hooks/use-tenant'
import { useEvents } from '@/hooks/use-events'
import { useChekiForms } from '@/hooks/use-cheki'
import { useMembers } from '@/hooks/use-members'
import { useMusicReleases } from '@/hooks/use-music'
import { Typewriter } from '@/components/ui/typewriter'
import { formatDate } from '@/lib/utils'

const EVENT_TYPE_LABELS: Record<string, string> = {
  live_show: 'Live Show',
  meet_greet: 'Meet & Greet',
  cheki_session: 'Sesi Cheki',
  fan_meeting: 'Fan Meeting',
  online_event: 'Online',
}

export default function DashboardPage() {
  const { data: tenantData, isLoading: loadingTenant } = useTenant()
  const { data: events = [] } = useEvents()
  const { data: chekiForms = [] } = useChekiForms()
  const { data: members = [] } = useMembers()
  const { data: releases = [] } = useMusicReleases()

  const now = new Date()

  // Stats
  const publishedEvents = events.filter((e) => e.is_published).length
  const upcomingEvents = events.filter(
    (e) => e.is_published && new Date(e.starts_at) > now
  ).length
  const openForms = chekiForms.filter((f) => f.status === 'open').length
  const totalOrders = chekiForms.reduce((s, f) => s + (f.order_count ?? 0), 0)
  const totalMembers = members.length
  const publishedReleases = releases.filter((r) => r.is_published).length

  // Content sections
  const nextEvents = events
    .filter((e) => e.is_published && new Date(e.starts_at) > now)
    .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())
    .slice(0, 3)

  const activeChekiForms = chekiForms.filter((f) => f.status === 'open')

  if (loadingTenant) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-muted-foreground">Memuat...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">
          Selamat datang, {tenantData?.tenant?.name ?? 'Admin'}
        </h1>
        <p className="text-muted-foreground flex items-center gap-1 flex-wrap">
          Kelola{' '}
          <Typewriter
            text={['event', 'member', 'cheki', 'konten publik', 'halaman artis']}
            speed={60}
            deleteSpeed={35}
            waitTime={1800}
            className="text-primary font-medium"
            cursorChar="_"
          />
          {' '}idol kamu dari sini.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard label="Event Aktif"      value={publishedEvents}   icon={CalendarDays}  />
        <StatCard label="Event Mendatang"  value={upcomingEvents}    icon={CalendarClock} />
        <StatCard label="Form Cheki Aktif" value={openForms}         icon={ClipboardList} />
        <StatCard label="Total Pesanan"    value={totalOrders}       icon={ShoppingBag}   />
        <StatCard label="Total Member"     value={totalMembers}      icon={Users}         />
        <StatCard label="Rilis Musik"      value={publishedReleases} icon={Music2}        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Link
          to="/events"
          className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 shadow-sm hover:bg-accent hover:border-primary/30 transition-colors"
        >
          <div className="rounded-lg bg-primary/10 p-2 text-primary shrink-0">
            <Plus className="h-4 w-4" />
          </div>
          <span className="text-sm font-medium">Tambah Event</span>
        </Link>
        <Link
          to="/cheki"
          className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 shadow-sm hover:bg-accent hover:border-primary/30 transition-colors"
        >
          <div className="rounded-lg bg-primary/10 p-2 text-primary shrink-0">
            <Camera className="h-4 w-4" />
          </div>
          <span className="text-sm font-medium">Form Cheki</span>
        </Link>
        <Link
          to="/members"
          className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 shadow-sm hover:bg-accent hover:border-primary/30 transition-colors"
        >
          <div className="rounded-lg bg-primary/10 p-2 text-primary shrink-0">
            <Users className="h-4 w-4" />
          </div>
          <span className="text-sm font-medium">Member</span>
        </Link>
        <Link
          to="/music"
          className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 shadow-sm hover:bg-accent hover:border-primary/30 transition-colors"
        >
          <div className="rounded-lg bg-primary/10 p-2 text-primary shrink-0">
            <Music2 className="h-4 w-4" />
          </div>
          <span className="text-sm font-medium">Musik</span>
        </Link>
      </div>

      {/* Two-Column Content */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left: Upcoming Events */}
        <div className="rounded-xl border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h2 className="font-semibold">Event Mendatang</h2>
            <Link to="/events" className="text-xs text-primary hover:underline">
              Lihat semua
            </Link>
          </div>
          {nextEvents.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">
              Tidak ada event mendatang.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {nextEvents.map((event) => (
                <li key={event.id} className="px-5 py-4 space-y-1.5">
                  <p className="font-medium text-sm leading-snug">{event.title}</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                      {formatDate(event.starts_at, { dateStyle: 'medium', timeStyle: 'short' })}
                    </span>
                    {event.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5 shrink-0" />
                        {event.location}
                      </span>
                    )}
                  </div>
                  {event.event_type && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      <Tag className="h-3 w-3" />
                      {EVENT_TYPE_LABELS[event.event_type] ?? event.event_type}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Right: Active Cheki Forms */}
        <div className="rounded-xl border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h2 className="font-semibold">Form Cheki Aktif</h2>
            <Link to="/cheki" className="text-xs text-primary hover:underline">
              Lihat semua
            </Link>
          </div>
          {activeChekiForms.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">
              Tidak ada form cheki yang sedang buka.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {activeChekiForms.map((form) => {
                const hasQuota = form.total_quota != null
                const pct = hasQuota
                  ? Math.min(100, Math.round((form.booked_count / form.total_quota!) * 100))
                  : 0

                return (
                  <li key={form.id} className="px-5 py-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-sm leading-snug">{form.title}</p>
                      <span className="shrink-0 inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                        Buka
                      </span>
                    </div>
                    {form.events && (
                      <p className="text-xs text-muted-foreground">{form.events.title}</p>
                    )}
                    {hasQuota ? (
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Kuota terpakai</span>
                          <span>{form.booked_count} / {form.total_quota}</span>
                        </div>
                        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                          <div
                            className="h-full rounded-full bg-primary transition-all"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {form.booked_count} terpesan · kuota tidak terbatas
                      </p>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

function StatCard({
  label,
  value,
  icon: Icon,
}: {
  label: string
  value: number
  icon: React.ElementType
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-foreground">{label}</p>
        <div className="rounded-lg bg-primary/10 p-2 text-primary">
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <p className="text-3xl font-bold tabular-nums">{value}</p>
    </div>
  )
}
